import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import { getHardcodedColor, toPercentage } from '../../utils';

interface StackBarChartProps {
    data: { [key: string]: number }[];
    cwidth: number;
    cheight: number;
    domain?: [number, number];
    percentage?: boolean;
    showValues?: boolean;
}

const StackedBar: React.FC<StackBarChartProps> = ({ data, cwidth, cheight, domain = [-1, 1], percentage = true, showValues = true }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        // Clear the canvas
        context.clearRect(0, 0, cwidth, cheight);

        // Derive width, height, and bar spacing
        const padding = 3;
        const barSpacing = 1;
        const barHeight = (cheight - padding * 2 - (data.length - 1) * barSpacing) / data.length;
        const xScale = d3.scaleLinear().domain(domain).range([0, cwidth]);

        // Iterate through each data item to draw a bar
        data.forEach((item, index) => {
            // Determine vertical position for this bar
            const yPosition = padding + index * (barHeight + barSpacing);

            // Calculate zero position
            const zeroPosition = xScale(0);

            let x0 = zeroPosition; // Positive stacking start
            let x1 = zeroPosition; // Negative stacking start

            Object.entries(item).forEach(([key, value]) => {
                const barWidth = xScale(Math.abs(value)) - zeroPosition;

                if (index !== 0) {
                    const baseColor = getHardcodedColor(key); 
                    const [r, g, b] = [
                        parseInt(baseColor.slice(1, 3), 16),
                        parseInt(baseColor.slice(3, 5), 16),
                        parseInt(baseColor.slice(5, 7), 16),
                    ];
                    context.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.4})`;
                } else {
                    // Set the fill color
                    context.fillStyle = getHardcodedColor(key);
                }

                if (value < 0) {
                    // Draw negative bar
                    context.fillRect(x1 - barWidth, yPosition, Math.abs(barWidth), barHeight);
                    if (showValues && value <= -0.01) {
                        // Add value inside the bar
                        context.fillStyle = 'white';
                        context.textAlign = 'center';
                        context.textBaseline = 'middle';
                        context.fillText(
                            percentage ? toPercentage(value) : value.toFixed(2),
                            x1 - barWidth / 2,
                            yPosition + barHeight / 2
                        );
                    }
                    x1 -= barWidth;
                } else {
                    // Draw positive bar
                    context.fillRect(x0, yPosition, barWidth, barHeight);
                    if (showValues && value >= 0.01) {
                        // Add value inside the bar
                        context.fillStyle = 'white';
                        context.textAlign = 'center';
                        context.textBaseline = 'middle';
                        context.fillText(
                            percentage ? toPercentage(value) : value.toFixed(2),
                            x0 + barWidth / 2,
                            yPosition + barHeight / 2
                        );
                    }
                    x0 += barWidth;
                }
            });

            // Draw the zero line
            context.beginPath();
            context.setLineDash([5, 5]);
            context.moveTo(zeroPosition, yPosition);
            context.lineTo(zeroPosition, yPosition + barHeight);
            context.strokeStyle = '#000';
            context.stroke();
        });
    }, [data, cwidth, cheight, showValues]);

    return (
        <canvas
            ref={canvasRef}
            width={cwidth}
            height={cheight}
        />
    );
};

export default StackedBar;
